//TicketList.js

import * as React from "react";
import { useNavigate } from "react-router-dom";
import MUIDataTable, {
  MUIDataTableColumn,
  MUIDataTableOptions,
} from "mui-datatables";
import { createTheme, ThemeProvider, Theme } from "@mui/material/styles";
import {
  Box,
  Button,
  Card,
  IconButton,
  Tooltip,
  Grid,
  FormControl,
  Autocomplete,
  TextField,
} from "@mui/material";
import { Edit as EditIcon } from "@mui/icons-material";
import axios from "../../api";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import SoftBox from "components/SoftBox";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import SoftButton from "components/SoftButton";
import { useSelector } from "react-redux";
import { useSnackbar } from "components/AlertMessages/SnackbarContext";
import RatingComponent from "./Rating";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SoftTypography from "components/SoftTypography";
import { RootState } from "store";
import SlideOverPanel from "components/SlideOverPanel";
import AddTicket from "./AddTicket";
import EditTicket from "./EditTicket";

const getMuiTheme = (theme: Theme): Theme =>
  createTheme({
    components: {
      MUIDataTable: {
        styleOverrides: {
          responsiveScroll: {
            maxHeight: "unset",
            overflowX: "auto",
          },
        },
      },
    },
  });

interface TicketStatus {
  status_id: number;
  status_name: string;
}

interface Divisions {
  division_id: number;
  division_name: string;
}

interface AssetTypeOption {
  asset_type_id: number;
  asset_type_name: string;
}

// Custom cell renderers moved outside the component

const DescriptionCell: React.FC<{ value: string }> = ({ value }) => {
  const displayValue =
    value?.length > 20 ? `${value.substring(0, 20)}...` : value;
  return (
    <Tooltip title={value}>
      <span>{displayValue}</span>
    </Tooltip>
  );
};

interface AddressCellProps {
  value: string;
  tableMeta: any;
}

const AddressCell: React.FC<AddressCellProps> = ({ value, tableMeta }) => {
  const { currentTableData, rowIndex } = tableMeta;
  const data = tableMeta?.tableData[currentTableData[rowIndex]?.index];
  const city = data?.city_name;
  const state = data?.state_name;
  const addressType = data?.address_type;
  const addressData = `${value}, ${city}, ${state},${addressType}`;

  const displayValue =
    addressData?.length > 20
      ? `${addressData.substring(0, 20)}...`
      : addressData;
  return (
    <Tooltip title={addressData}>
      <span>{displayValue}</span>
    </Tooltip>
  );
};

interface ActionsCellProps {
  tableMeta: any;
  onNavigate: (path: string) => void;
  onEditTicket: (ticketId: number) => void;
  onEscalate: (ticketId: number) => void;
  onCancel: (ticketId: number) => void;
  onRatingOpen: (data: any) => void;
}

const ActionsCell: React.FC<ActionsCellProps> = ({
  tableMeta,
  onNavigate,
  onEditTicket,
  onEscalate,
  onCancel,
  onRatingOpen,
}) => {
  const { currentTableData, rowIndex } = tableMeta;
  const ticketData = tableMeta?.tableData[currentTableData[rowIndex]?.index];

  const canEscalate =
    [3, 4, 5].includes(ticketData?.status_id) && !ticketData?.is_escalated;

  const hasAssignedEmployee = ticketData?.assigned_employee_id;
  const canRate = ticketData?.status_id === 6;
  const isCancelled = ticketData?.status_id === 9;

  return (
    <>
      <IconButton
        className="mt-eye-btn"
        aria-label="view"
        onClick={() =>
          onNavigate(`/customer/ticket-view/${ticketData.ticket_id}`)
        }
      >
        <VisibilityIcon />
      </IconButton>

      {canEscalate && (
        <Button
          sx={{
            position: "relative",
          }}
          variant="contained"
          size="small"
          onClick={() => onEscalate(ticketData?.ticket_id)}
        >
          Escalation
        </Button>
      )}

      {hasAssignedEmployee ? (
        canRate && (
          <Button
            sx={{
              position: "relative",
            }}
            variant="contained"
            size="small"
            onClick={() => onRatingOpen(ticketData)}
          >
            Rating
          </Button>
        )
      ) : (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
          }}
        >
          <IconButton
            aria-label="edit"
            onClick={() => onEditTicket(ticketData?.ticket_id)}
          >
            <EditIcon />
          </IconButton>
          {!isCancelled && (
            <Button
              sx={{
                position: "relative",
              }}
              variant="contained"
              size="small"
              onClick={() => onCancel(ticketData?.ticket_id)}
            >
              Cancel
            </Button>
          )}
        </Box>
      )}
    </>
  );
};

const TicketList = () => {
  const { fetchError, fetchSuccess } = useSnackbar();
  const navigate = useNavigate();
  const [rows, setRows] = React.useState<
    { is_escalated: boolean; [key: string]: any }[]
  >([]);
  const user = useSelector((state: RootState) => state.auth.user);
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [ticketData, setTicketData] = React.useState(null);
  const [statusFilter, setStatusFilter] = React.useState<number | null>(null);
  const [divisionsFilter, setDivisionsFilter] = React.useState<number | null>(
    null
  );
  const [assetTypeFilter, setAssetTypeFilter] = React.useState<AssetTypeOption | null>(null);

  const [ticketStatuses, setTicketStatuses] = React.useState<TicketStatus[]>(
    []
  );
  const [divisions, setDivisions] = React.useState<Divisions[]>([]);
  const [assetTypes, setAssetTypes] = React.useState<AssetTypeOption[]>([]);
  const [closedStatusId, setClosedStatusId] = React.useState(null);
  const [addPanelOpen, setAddPanelOpen] = React.useState(false);
  const [editPanelOpen, setEditPanelOpen] = React.useState(false);
  const [selectedTicketId, setSelectedTicketId] = React.useState<number | null>(null);

  const tableOptions: MUIDataTableOptions = {
    selectableRows: "none",
    selectableRowsHeader: false,
    elevation: 0,
    tableBodyHeight: "auto",
    responsive: "standard",
    setRowProps: (row, dataIndex) => {
      const isEscalated = rows[dataIndex]?.is_escalated;
      return {
        style: {
          backgroundColor: isEscalated ? "#ffaba7" : undefined,
        },
      };
    },
  };
  const handleTicketCancel = async (ticket_id: number) => {
    const ticketData = {
      status_id: 9,
    };
    try {
      await axios.put(`/tickets/${ticket_id}`, { ticketData });
      fetchData();
      fetchSuccess("Ticket Cancelled Successfully");
    } catch (err) {
      console.error("Error: ", err);
      fetchError("Unable to Cancel Ticket");
    }
  };

  const handleEscalationUpdate = async (ticket_id: number) => {
    const ticketData = {
      is_escalated: 1,
    };
    try {
      await axios.put(`/tickets/escalation/${ticket_id}`, ticketData);
      fetchData();
      fetchSuccess("Ticket escalated Successfully");
    } catch (err) {
      console.error("Error: ", err);
      fetchError("Unable to escalate Ticket");
    }
  };

  const handleRatingOpen = (data: React.SetStateAction<null>) => {
    setTicketData(data);
    setIsModalOpen(true);
  };

  const handleRatingClose = () => {
    setIsModalOpen(false);
  };

  const fetchStatuses = async () => {
    try {
      const response = await axios.get("/ticket-statuses");
      setTicketStatuses(response.data);

      const closedStatus = response.data.find(
        (status: { status_name: string }) =>
          status.status_name.toLowerCase() === "closed"
      );
      if (closedStatus) {
        setClosedStatusId(closedStatus.status_id);
      }
    } catch (error) {
      console.error("Error fetching statuses:", error);
    }
  };

  const fetchDivisions = async () => {
    try {
      const response = await axios.get(`/divisions/customer/${user?.userId}`);
      setDivisions(response?.data?.divisions);
    } catch (error) {
      console.error("Error fetching statuses:", error);
    }
  };

  const fetchAssetTypes = React.useCallback(async () => {
    try {
      const res = await axios.get("/asset-types");
      const list = res.data?.list ?? [];
      setAssetTypes(
        list.map((a: AssetTypeOption) => ({
          asset_type_id: a.asset_type_id,
          asset_type_name: a.asset_type_name ?? "",
        }))
      );
    } catch (e) {
      console.error("Failed to fetch asset types", e);
    }
  }, []);

  React.useEffect(() => {
    fetchAssetTypes();
  }, [fetchAssetTypes]);

  const fetchData = React.useCallback(async () => {
    try {
      const response = await axios.get(`/tickets/customer/${user?.userId}`);
      const allTickets = response?.data?.list || [];

      let filteredTickets = allTickets;

      // hide closed by default
      if (!statusFilter && closedStatusId) {
        filteredTickets = filteredTickets.filter(
          (ticket: { status_id: number }) => ticket.status_id !== closedStatusId
        );
      }

      // status filter
      if (statusFilter) {
        filteredTickets = filteredTickets.filter(
          (ticket: { status_id: number }) => ticket.status_id === statusFilter
        );
      }

      // division filter
      if (divisionsFilter) {
        filteredTickets = filteredTickets.filter(
          (ticket: { division_id: number }) =>
            ticket.division_id === divisionsFilter
        );
      }

      // asset type filter
      if (assetTypeFilter) {
        filteredTickets = filteredTickets.filter(
          (ticket: { asset_type_id: number }) =>
            ticket.asset_type_id === assetTypeFilter.asset_type_id
        );
      }

      setRows(filteredTickets);
    } catch (error) {
      console.error(error);
      setRows([]);
    }
  }, [user?.userId, statusFilter, divisionsFilter, assetTypeFilter, closedStatusId]);

  React.useEffect(() => {
    fetchStatuses();
    fetchDivisions();
  }, []);

  React.useEffect(() => {
    if (closedStatusId !== null) {
      fetchData();
    }
  }, [fetchData, closedStatusId]);

  // Render functions that use the component instead of inline JSX
  const renderDescriptionCell = (value: string) => {
    return <DescriptionCell value={value} />;
  };

  const renderAddressCell = (value: string, tableMeta: any) => {
    return <AddressCell value={value} tableMeta={tableMeta} />;
  };

  const handleEditTicket = (ticketId: number) => {
    setSelectedTicketId(ticketId);
    setEditPanelOpen(true);
  };

  const renderActionsCell = (value: any, tableMeta: any) => {
    return (
      <ActionsCell
        tableMeta={tableMeta}
        onNavigate={navigate}
        onEditTicket={handleEditTicket}
        onEscalate={handleEscalationUpdate}
        onCancel={handleTicketCancel}
        onRatingOpen={handleRatingOpen}
      />
    );
  };

  const columns: MUIDataTableColumn[] = [
    { name: "title", label: "Title" },
    ...(user?.type === "company" && user?.division_id === null
      ? [{ name: "division_name", label: "Division" }]
      : []),
    {
      name: "asset_name",
      label: "Brand Name",
    },
    { name: "asset_type_name", label: "Asset Type" },
    {
      name: "description",
      label: "Description",
      options: {
        filter: false,
        customBodyRender: renderDescriptionCell,
      },
    },
    {
      name: "address",
      label: "Address",
      options: {
        filter: false,
        customBodyRender: renderAddressCell,
      },
    },
    { name: "employee_name", label: "Assignee" },
    { name: "status_name", label: "Status" },
    {
      name: "id",
      label: "Actions",
      options: {
        filter: false,
        sort: false,
        customBodyRender: renderActionsCell,
      },
    },
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Card
        style={{ padding: "24px", margin: "16px auto", maxWidth: "2000px" }}
      >
        <Grid
          container
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
        >
          <Grid container item xs={12} sm={6} spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <SoftTypography variant="caption" fontWeight="bold" sx={{ mb: 0.5 }}>
                  Status
                </SoftTypography>
                <Autocomplete
                  options={ticketStatuses}
                  getOptionLabel={(option) => option.status_name}
                  value={ticketStatuses?.find((s) => s.status_id === statusFilter) ?? null}
                  onChange={(event, value) => {
                    setStatusFilter(value?.status_id ?? null);
                  }}
                  renderInput={(params) => (
                    <TextField {...params} variant="outlined" size="small" />
                  )}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <SoftTypography variant="caption" fontWeight="bold" sx={{ mb: 0.5 }}>
                  Asset Type
                </SoftTypography>
                <Autocomplete
                  options={assetTypes}
                  getOptionLabel={(o) => o.asset_type_name}
                  value={assetTypeFilter}
                  onChange={(_, v) => setAssetTypeFilter(v)}
                  renderInput={(params) => (
                    <TextField {...params} variant="outlined" size="small" placeholder="All" />
                  )}
                />
              </FormControl>
            </Grid>
            {user?.type === "company" && user?.division_id === null && (
              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth>
                  <SoftTypography variant="caption" fontWeight="bold" sx={{ mb: 0.5 }}>
                    Divisions
                  </SoftTypography>
                  <Autocomplete
                    options={divisions}
                    getOptionLabel={(option) => option.division_name}
                    value={divisions?.find((d) => d.division_id === divisionsFilter) ?? null}
                    onChange={(event, value) => {
                      setDivisionsFilter(value?.division_id ?? null);
                    }}
                    renderInput={(params) => (
                      <TextField {...params} variant="outlined" size="small" placeholder="All" />
                    )}
                  />
                </FormControl>
              </Grid>
            )}
          </Grid>

          <Grid
            item
            xs={12}
            sm={6}
            style={{ display: "flex", justifyContent: "flex-end" }}
          >
            <SoftButton
              variant="gradient"
              color="info"
              onClick={() => setAddPanelOpen(true)}
            >
              Add Ticket
            </SoftButton>
          </Grid>
        </Grid>

        <SoftBox
          sx={{
            "& .MuiTableRow-root:not(:last-child)": {
              "& td": (theme: Theme) => ({
                borderBottom: `${theme.borders.borderWidth[1]} solid ${theme.borders.borderColor}`,
              }),
            },
          }}
          className="manage-tickets"
        >
          <ThemeProvider theme={getMuiTheme}>
            <MUIDataTable
              title={"Manage Tickets"}
              data={rows}
              columns={columns}
              options={tableOptions}
            />
          </ThemeProvider>
        </SoftBox>
      </Card>
      {isModalOpen && (
        <RatingComponent
          onClose={handleRatingClose}
          isOpen={isModalOpen}
          ratingData={ticketData}
          fetchData={fetchData}
        />
      )}
      <SlideOverPanel
        open={addPanelOpen}
        onClose={() => setAddPanelOpen(false)}
        title="Add Ticket"
        width={1000}
      >
        <AddTicket
          embedded
          onSuccess={() => {
            setAddPanelOpen(false);
            fetchData();
          }}
          onCancel={() => setAddPanelOpen(false)}
        />
      </SlideOverPanel>
      <SlideOverPanel
        open={editPanelOpen}
        onClose={() => {
          setEditPanelOpen(false);
          setSelectedTicketId(null);
        }}
        title="Edit Ticket"
        width={1000}
      >
        {selectedTicketId != null && (
          <EditTicket
            id={String(selectedTicketId)}
            embedded
            onSuccess={() => {
              setEditPanelOpen(false);
              setSelectedTicketId(null);
              fetchData();
            }}
            onCancel={() => {
              setEditPanelOpen(false);
              setSelectedTicketId(null);
            }}
          />
        )}
      </SlideOverPanel>
    </DashboardLayout>
  );
};

export default TicketList;
